// Updated QuizSession04.js component with dashboard aesthetic matching Session 2
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { updateSessionProgress } from "../../supabase";

// SVG Check Mark Component - Updated to match dashboard
const CheckMarkSVG = ({ size = 16, color = "#374151" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const QuizSession04 = ({ 
  showAnswers, 
  reviewMode = false, 
  onComplete, 
  resetQuiz, 
  requireCorrectAnswer = false
}) => {
  const { user } = useAuth();
  const [score, setScore] = useState(0);
  const [timeTaken, setTimeTaken] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const currentSessionNumber = 4;

  useEffect(() => {
    if (resetQuiz) {
      console.log("🔄 Resetting quiz state");
      setQuizCompleted(false);
      setUserAnswers([]);
      setCurrentQuestionIndex(0);
      setStartTime(Date.now());
      setIsFinished(false);
      setScore(0);
      setTimeTaken(0);
      setFeedback("");
      setSaveError(null);
    }
  }, [resetQuiz]);

  const questions = [
    {
      question: "What does 'acceptance' mean in the context of managing chronic orofacial pain?",
      options: [
        "Giving up and resigning yourself to a life of suffering.",
        "Allowing difficult experiences to exist without struggling against them, while still taking action to improve your life.",
        "Pretending that pain doesn't affect you.",
        "Never seeking medical treatment for your pain."
      ],
      correctAnswer: "Allowing difficult experiences to exist without struggling against them, while still taking action to improve your life.",
      explanation: "Acceptance doesn't mean giving up - it means acknowledging pain without fighting it constantly, which actually frees up energy to focus on what you can control and what matters to you.",
      explanations: {
        "Giving up and resigning yourself to a life of suffering.": "Try again. Acceptance is not about giving up - it's about changing how you relate to difficult experiences.",
        "Allowing difficult experiences to exist without struggling against them, while still taking action to improve your life.": "Correct! Acceptance means acknowledging pain without constant struggle, freeing energy for meaningful activities.",
        "Pretending that pain doesn't affect you.": "Try again. Acceptance involves acknowledging pain's impact, not pretending it doesn't exist.",
        "Never seeking medical treatment for your pain.": "Try again. Acceptance doesn't mean avoiding helpful medical care."
      }
    },
    {
      question: "How is acceptance different from 'giving up' when dealing with chronic pain?",
      options: [
        "There is no difference - they are the same thing.",
        "Acceptance involves acknowledging pain while still engaging in meaningful activities; giving up means stopping all efforts to live well.",
        "Acceptance means never feeling frustrated about pain.",
        "Giving up is better because it requires less effort."
      ],
      correctAnswer: "Acceptance involves acknowledging pain while still engaging in meaningful activities; giving up means stopping all efforts to live well.",
      explanation: "Acceptance is an active choice to work with pain rather than against it, while continuing to pursue what matters to you. Giving up involves withdrawing from life and meaningful activities.",
      explanations: {
        "There is no difference - they are the same thing.": "Try again. Acceptance and giving up are very different approaches to dealing with pain.",
        "Acceptance involves acknowledging pain while still engaging in meaningful activities; giving up means stopping all efforts to live well.": "Correct! Acceptance allows for continued engagement in life despite pain.",
        "Acceptance means never feeling frustrated about pain.": "Try again. Acceptance doesn't eliminate all difficult emotions - it changes how we relate to them.",
        "Giving up is better because it requires less effort.": "Try again. While giving up might seem easier short-term, it often leads to more suffering long-term."
      }
    },
    {
      question: "What is the 'finger trap' analogy in relation to chronic pain?",
      options: [
        "Pain is like a trap that you can never escape from.",
        "The harder you struggle against pain, the more stuck you become - like a finger trap that tightens when you pull.",
        "Pain traps are medical devices used to measure finger pain.",
        "You should always fight against pain with maximum effort."
      ],
      correctAnswer: "The harder you struggle against pain, the more stuck you become - like a finger trap that tightens when you pull.",
      explanation: "Just like a finger trap tightens when you pull hard but loosens when you relax, struggling against pain often makes it worse, while acceptance allows for more flexibility and freedom.",
      explanations: {
        "Pain is like a trap that you can never escape from.": "Try again. The analogy is about how our response to pain affects our experience, not about being permanently trapped.",
        "The harder you struggle against pain, the more stuck you become - like a finger trap that tightens when you pull.": "Correct! This analogy illustrates how fighting pain often makes suffering worse.",
        "Pain traps are medical devices used to measure finger pain.": "Try again. This is not what the finger trap analogy refers to.",
        "You should always fight against pain with maximum effort.": "Try again. This misses the point of the analogy - that fighting harder often makes things worse."
      }
    },
    {
      question: "How can practicing acceptance help with re-engaging in meaningful life activities?",
      options: [
        "It eliminates all pain so you can do everything you used to do.",
        "It helps you focus energy on what you can control rather than fighting what you can't control.",
        "It teaches you to avoid all activities that might cause pain.",
        "It makes you stop caring about activities you used to enjoy."
      ],
      correctAnswer: "It helps you focus energy on what you can control rather than fighting what you can't control.",
      explanation: "When you stop using all your energy fighting pain, you have more resources available to engage in activities that bring meaning and joy to your life, even if you do them differently than before.",
      explanations: {
        "It eliminates all pain so you can do everything you used to do.": "Try again. Acceptance doesn't eliminate pain - it changes your relationship with it.",
        "It helps you focus energy on what you can control rather than fighting what you can't control.": "Correct! Acceptance frees up mental and emotional energy for meaningful activities.",
        "It teaches you to avoid all activities that might cause pain.": "Try again. Acceptance often helps people gradually re-engage with activities, not avoid them.",
        "It makes you stop caring about activities you used to enjoy.": "Try again. Acceptance helps you find ways to stay connected to what matters to you."
      }
    },
    {
      question: "When setting 'Goals for Re-engaging in Life,' what is the best approach?",
      options: [
        "Set goals that are exactly the same as before your pain started.",
        "Avoid setting any goals because pain makes them impossible.",
        "Set realistic, meaningful goals that consider your current situation and values.",
        "Only set goals related to managing pain, nothing else."
      ],
      correctAnswer: "Set realistic, meaningful goals that consider your current situation and values.",
      explanation: "Effective goal-setting involves finding a balance between what's realistic given your current situation and what's meaningful to you, allowing for adaptation while staying connected to your values.",
      explanations: {
        "Set goals that are exactly the same as before your pain started.": "Try again. While some goals might be the same, others may need to be adapted to your current situation.",
        "Avoid setting any goals because pain makes them impossible.": "Try again. Having meaningful goals is important for well-being, even with chronic pain.",
        "Set realistic, meaningful goals that consider your current situation and values.": "Correct! Good goals balance what's possible with what's meaningful to you.",
        "Only set goals related to managing pain, nothing else.": "Try again. While pain management is important, having broader life goals is also valuable for overall well-being."
      }
    }
  ];

  const handleFinishQuiz = async () => {
    // In review mode, just call onComplete to exit
    if (reviewMode) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    console.log(`🔍 Starting quiz submission for Session ${currentSessionNumber}...`);
    
    if (!user) {
      console.error("❌ No user found for quiz submission");
      setSaveError("You must be logged in to submit quiz results!");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const endTime = Date.now();
    const timeTakenInSeconds = (endTime - startTime) / 1000;

    let calculatedScore = 0;
    questions.forEach((question, index) => {
      if (userAnswers[index] === question.correctAnswer) {
        calculatedScore += 1;
      }
    });

    setScore(calculatedScore);
    setTimeTaken(Math.floor(timeTakenInSeconds));
    setIsFinished(true);
    setQuizCompleted(true);

    const quizData = {
      score: calculatedScore,
      answers: userAnswers,
      attempts: 1,
      totalQuestions: questions.length,
      timeTaken: Math.floor(timeTakenInSeconds)
    };

    const progressData = {
      quiz_completed: true,
      quiz_score: calculatedScore,
      session_completed: true,
      session_fully_completed: true,
      updated_at: new Date().toISOString()
    };

    console.log(`🔍 Progress data to save:`, progressData);
    console.log(`🔍 Quiz data for callback:`, quizData);

    try {
      console.log(`🔍 Attempting to save quiz results for Session ${currentSessionNumber}...`);
      const result = await updateSessionProgress(user.id, currentSessionNumber, progressData);
      
      if (result) {
        console.log('✅ Quiz results saved successfully');
        
        try {
          await updateSessionProgress(user.id, currentSessionNumber, {
            quiz_answers: userAnswers,
            quiz_attempts: 1,
            quiz_completion_time: new Date().toISOString(),
            quiz_total_questions: questions.length,
            quiz_time_taken_seconds: Math.floor(timeTakenInSeconds)
          });
          console.log('✅ Extended quiz data saved successfully');
        } catch (extendedError) {
          console.warn('⚠️ Extended quiz data save failed, but core data saved:', extendedError);
        }
        
        // FIX: Call onComplete immediately like Sessions 1 and 2
        if (onComplete) {
          console.log(`✅ Quiz finished for Session ${currentSessionNumber} - calling onComplete callback`);
          onComplete(quizData);
        }
        
      } else {
        throw new Error('Failed to save quiz results to database');
      }
      
    } catch (error) {
      console.error("❌ Error saving quiz result:", error);
      setSaveError(`Error saving quiz result: ${error.message}`);
      
      // Still call onComplete even if save failed
      if (onComplete) {
        console.log(`✅ Quiz finished for Session ${currentSessionNumber} (despite save error) - calling onComplete callback`);
        onComplete(quizData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnswerSelection = (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const updatedAnswers = [...userAnswers];
    updatedAnswers[currentQuestionIndex] = answer;
    setUserAnswers(updatedAnswers);
    
    // Show feedback based on the selected answer
    setFeedback(currentQuestion.explanations[answer]);
  };

  const handleNextQuestion = () => {
    // In requireCorrectAnswer mode, check if current answer is correct
    if (requireCorrectAnswer && !reviewMode) {
      const currentQuestion = questions[currentQuestionIndex];
      const selectedAnswer = userAnswers[currentQuestionIndex];
      
      if (!selectedAnswer || selectedAnswer !== currentQuestion.correctAnswer) {
        return; // Don't proceed if no answer selected or answer is incorrect
      }
    }

    setFeedback("");
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    setFeedback("");
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
    }
  };

  // Get current question data
  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = userAnswers[currentQuestionIndex];
  const isCorrectAnswer = selectedAnswer === currentQuestion.correctAnswer;

  // Determine if Next/Finish button should be enabled
  const canProceed = reviewMode || // Always can proceed in review mode
                    !requireCorrectAnswer || // Can proceed if not requiring correct answers
                    (selectedAnswer && isCorrectAnswer); // Can proceed if correct answer selected

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isFinished || reviewMode ? (
        <div>
          {/* Question Header - Updated styling to match dashboard */}
          <div style={{
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              backgroundColor: '#F8F9FA',
              padding: '4px 8px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              display: 'inline-block'
            }}>
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 20px 0',
              lineHeight: '1.4',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              {currentQuestion.question}
            </h3>
          </div>

          {/* Answer Options - Dashboard colors only, no red */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option === currentQuestion.correctAnswer;
              
              let buttonColor = '#374151';
              let backgroundColor = '#FFFFFF';
              let borderColor = '#E5E7EB';
              let showCheckmark = false;

              if (reviewMode) {
                // Review mode: only highlight correct answers in green
                if (isCorrect) {
                  backgroundColor = '#ECFDF5';
                  borderColor = '#10B981';
                  buttonColor = '#374151';
                  showCheckmark = true;
                } else {
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              } else {
                // Normal mode: show selection and correct feedback
                if (isSelected) {
                  if (feedback) {
                    // Show feedback colors only for the selected answer
                    if (isCorrect) {
                      backgroundColor = '#ECFDF5';
                      borderColor = '#10B981';
                      buttonColor = '#374151';
                      showCheckmark = true;
                    } else {
                      // Incorrect answers get neutral gray styling - no red
                      backgroundColor = '#F8F9FA';
                      borderColor = '#D1D5DB';
                      buttonColor = '#374151';
                    }
                  } else {
                    // Just show selection in blue
                    backgroundColor = '#EFF6FF';
                    borderColor = '#3B82F6';
                    buttonColor = '#1B365D';
                  }
                } else {
                  // Default state for all unselected answers
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => !reviewMode && handleAnswerSelection(option)}
                  disabled={reviewMode}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelectStart={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '1.4',
                    minHeight: '60px',
                    color: buttonColor,
                    backgroundColor: backgroundColor,
                    border: `2px solid ${borderColor}`,
                    cursor: reviewMode ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#F8F9FA';
                      e.target.style.borderColor = '#D1D5DB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#FFFFFF';
                      e.target.style.borderColor = '#E5E7EB';
                    }
                  }}
                >
                  {showCheckmark && (
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <CheckMarkSVG size={10} color="#FFFFFF" />
                    </div>
                  )}
                  <span 
                    style={{ 
                      flex: 1,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    {option}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Show explanation in review mode or after feedback */}
          {reviewMode && currentQuestion.explanation && (
            <div style={{
              padding: '16px',
              backgroundColor: '#F0F9FF',
              borderRadius: '12px',
              border: '1px solid #E0E7FF',
              marginBottom: '24px'
            }}>
              <div style={{ 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151',
                fontSize: '14px'
              }}>
                Why this is correct:
              </div>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4'
              }}>
                {currentQuestion.explanation}
              </div>
            </div>
          )}

          {/* Feedback area - green for correct, light blue for incorrect */}
          {feedback && !reviewMode && (
            <div style={{
              padding: '16px',
              backgroundColor: feedback.includes("Correct") ? "#ECFDF5" : "#DBEAFE",
              borderRadius: '12px',
              border: feedback.includes("Correct") ? '1px solid #10B981' : '1px solid #60A5FA',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4',
                fontWeight: '500'
              }}>
                {feedback}
              </div>
            </div>
          )}

          {/* Navigation - Updated to match dashboard styling */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              style={{
                backgroundColor: currentQuestionIndex === 0 ? '#F8F9FA' : '#FFFFFF',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: '24px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                boxShadow: currentQuestionIndex === 0 ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              Previous
            </button>

            {/* Progress Dots - Updated styling */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {questions.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: index === currentQuestionIndex ? '#1B365D' :
                      userAnswers[index] ? '#10B981' : '#E5E7EB',
                    border: '1px solid #E5E7EB',
                    transition: 'background-color 0.2s ease'
                  }}
                />
              ))}
            </div>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={handleNextQuestion}
                disabled={!canProceed}
                style={{
                  backgroundColor: canProceed ? '#1B365D' : '#F8F9FA',
                  color: canProceed ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: canProceed ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  opacity: canProceed ? 1 : 0.5,
                  boxShadow: canProceed ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleFinishQuiz}
                disabled={(!canProceed && !reviewMode) || isSaving}
                style={{
                  backgroundColor: (reviewMode || canProceed) ? '#1B365D' : '#F8F9FA',
                  color: (reviewMode || canProceed) ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (reviewMode || canProceed) && !isSaving ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (reviewMode || canProceed) && !isSaving ? 1 : 0.5,
                  boxShadow: (reviewMode || canProceed) && !isSaving ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                {isSaving && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #D1D5DB',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {isSaving ? 'Saving...' : reviewMode ? 'Finish Review' : 'Finish Quiz'}
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Simple Quiz Complete Screen like Sessions 1 and 2 */
        <div style={{ 
          textAlign: 'center', 
          padding: '24px 0',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#ECFDF5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            border: '1px solid #10B981'
          }}>
            <CheckMarkSVG size={24} color="#10B981" />
          </div>
          
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>
            Quiz Complete!
          </h3>
          
          {saveError && (
            <div style={{
              fontSize: '12px',
              color: '#374151',
              marginBottom: '12px',
              backgroundColor: '#F8F9FA',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
              display: 'inline-block'
            }}>
              {saveError}
            </div>
          )}
          
          <div style={{
            fontSize: '14px',
            color: '#6B7280',
            marginBottom: '16px',
            fontWeight: '500'
          }}>
            Score: {score} / {questions.length}
          </div>
          
          <div style={{
            backgroundColor: '#F8F9FA',
            color: '#374151',
            padding: '6px 12px',
            borderRadius: '16px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            border: '1px solid #E5E7EB'
          }}>
            <CheckMarkSVG size={12} color="#374151" />
            {saveError ? 'Continuing despite save error' : 'Results saved successfully'}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Remove all text selection and highlighting completely */
          *, *::before, *::after {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Extra prevention for buttons and spans */
          button, button *, span, span *, div, div *, p, p * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Remove focus outlines and tap highlights */
          button:focus, button:active, div:focus, div:active {
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
            box-shadow: none !important;
          }
          
          button::-moz-focus-inner {
            border: 0 !important;
            outline: 0 !important;
          }
          
          /* Remove all selection highlighting - multiple approaches */
          ::selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          ::-moz-selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          ::-webkit-selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          /* Prevent weird browser highlighting on mobile and desktop */
          button:active, button:focus, span:active, span:focus {
            background-color: inherit !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Prevent mouse selection events */
          button {
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
            -webkit-user-drag: none !important;
            -khtml-user-drag: none !important;
            -moz-user-drag: none !important;
            -o-user-drag: none !important;
            user-drag: none !important;
          }
        `
      }} />
    </div>
  );
};

export default QuizSession04;